"use client";

import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { motion, useInView, type Variants } from "framer-motion";
import NumberFlow from "@number-flow/react";
// This landing page's own navbar is intentionally not used. The site-wide
// navbar (components/home-v2/PrimaryNavigationSection) is mounted by
// app/(home)/page.tsx with theme="dark" instead, so navigation stays
// consistent with every other page. Kept here for reference only.
// import { NavigationSection } from "./NavigationSection";
import {HeroSection} from "./HeroSection";
import { BusinessOrganizationOverviewSection } from "./BusinessOrganizationOverviewSection";
import { BusinessSupportOptionsSection } from "./BusinessSupportOptionsSection";
import { ClientOnboardingProcessSection } from "./ClientOnboardingProcessSection";
import { ClientTrustAndTestimonialsSection } from "./ClientTrustAndTestimonialsSection";
import { EssentialBusinessServicesSection } from "./EssentialBusinessServicesSection";
import { GlobalBusinessSupportMapSection } from "./GlobalBusinessSupportMapSection";
import { ManagedCompanyServicesSection } from "./ManagedCompanyServicesSection";
import { ProviderComparisonIntroSection } from "./ProviderComparisonIntroSection";
import { ServiceApproachBenefitsSection } from "./ServiceApproachBenefitsSection";
import { SiteFooterSection } from "./SiteFooterSection";

const statistics = [
  { value: "3000+", label: "Companies Supported" },
  { value: "100+", label: "Countries Served globally" },
  { value: "10+", label: "Years of experience" },
];

const AnimatedStatValue = ({ value }: { value: string }): JSX.Element => {
  const match = value.match(/^(\d+)(.*)$/);
  const target = match ? Number(match[1]) : 0;
  const suffix = match ? match[2] : "";

  const ref = useRef<HTMLSpanElement>(null);
  // once: true so scrolling away and back does not replay the count-up.
  const isInView = useInView(ref, { once: true, amount: 0.6 });
  const [count, setCount] = useState(1);

  useEffect(() => {
    if (isInView) setCount(target);
  }, [isInView, target]);

  return (
    <span ref={ref}>
      <NumberFlow value={count} />
      {suffix}
    </span>
  );
};

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

export const    HomeV3 = (): JSX.Element => {
  return (
    // overflow-x-clip rather than overflow-hidden: `clip` does not create a
    // scroll container, so the sibling sticky navbar and the GSAP ScrollTrigger
    // pins in BusinessSupportOptionsSection keep measuring against the viewport.
    <main className="min-h-[80vh] w-full overflow-x-clip bg-black text-white">
      {/* <NavigationSection /> */}
      {/* <section className="relative">
        <img
          className="mx-auto block h-auto w-full max-w-[1440px]"
          alt="Eef d ba"
          src="/home-v3/e04ef299-767d-4b1a-b9b0-3cd75819bd75-1.png"
        />
      </section> */}
        <HeroSection />
      <section className="mx-auto flex max-w-[900px] flex-wrap items-center sm:items-start justify-center gap-x-16 gap-y-8 px-5 py-0 sm:gap-x-[65px] 2xl:max-w-[1100px] 2xl:gap-x-[90px] 3xl:max-w-[1280px] 3xl:gap-x-[120px]">
        {statistics.map((statistic) => (
          <div
            key={statistic.label}
            className="flex min-w-[120px] flex-col items-center gap-[18px] text-center sm:min-w-[155px] 2xl:min-w-[190px] 2xl:gap-[22px] 3xl:min-w-[220px]"
          >
            <p className="[font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-[34px] font-medium leading-[30px] sm:text-[50.2px] sm:leading-[42.8px] 2xl:text-[58px] 2xl:leading-[50px] 3xl:text-[64px] 3xl:leading-[55px]">
              <AnimatedStatValue value={statistic.value} />
            </p>
            <p className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-[15px] leading-[22px] text-[#585d70] sm:text-[20.3px] sm:leading-[27.4px] 2xl:text-[22px] 2xl:leading-[30px] 3xl:text-[24px] 3xl:leading-[33px]">
              {statistic.label}
            </p>
          </div>
        ))}
      </section>
    
      <BusinessOrganizationOverviewSection />
      <BusinessSupportOptionsSection />
      <section className="flex flex-col items-center sm:mt-12 px-5 py-0 text-center sm:py-16">
        <p className="animate-revolving-border rounded-[30px] bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px]">
          Our Signature SRC Method
        </p>
        <motion.h2
          className="mt-6 max-w-[937px] [background-image:radial-gradient(50%_50%_at_47%_71%,rgba(0,97,254,1)_35%,rgba(255,255,255,1)_100%)] bg-clip-text [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-4xl font-normal leading-[1.2] tracking-[-1.92px] text-transparent [text-shadow:0px_4px_24px_#0000000d] [-webkit-text-fill-color:transparent] sm:text-5xl sm:leading-[64px]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
          A simple system that brings Structure, Records, and Compliance
          together into one organized process.
        </motion.h2>
      </section>
      <ProviderComparisonIntroSection />
      <ServiceApproachBenefitsSection />
      <EssentialBusinessServicesSection />
      <ManagedCompanyServicesSection />
      <GlobalBusinessSupportMapSection />
      <ClientOnboardingProcessSection />
      <ClientTrustAndTestimonialsSection />
      <SiteFooterSection />
    </main>
  );
};
